/**
 * Validation findings, shown loudly and with the resolution.
 *
 * A blocker that only says "missing price" is useless. Every finding carries the
 * decision that would clear it, so the resolution line is rendered with the
 * message and is never collapsed away.
 */
import type { ValidationFinding } from '../lib/types';
import { Badge } from './Badge';
import { EmptyState } from './EmptyState';

const SEVERITY_ORDER: Record<string, number> = { blocker: 0, warning: 1, info: 2 };

export interface FindingListProps {
  findings: readonly ValidationFinding[];
  /** Shown when the list is empty. Omit to render nothing at all. */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Collapses repeated findings that differ only by line. */
  groupDuplicates?: boolean;
  /** Restrict to one severity, e.g. a blockers-only panel. */
  only?: ValidationFinding['severity'];
  /** Cap the list and say how many more there are. */
  limit?: number;
  className?: string;
}

export function FindingList({
  findings,
  emptyTitle,
  emptyDescription,
  groupDuplicates = true,
  only,
  limit,
  className,
}: FindingListProps) {
  let items = only ? findings.filter((f) => f.severity === only) : [...findings];

  if (groupDuplicates) {
    const seen = new Map<string, { finding: ValidationFinding; count: number }>();
    for (const finding of items) {
      const key = `${finding.severity}|${finding.code}|${finding.message}`;
      const existing = seen.get(key);
      if (existing) existing.count += 1;
      else seen.set(key, { finding, count: 1 });
    }
    items = [...seen.values()].map(({ finding, count }) =>
      count > 1 ? { ...finding, message: `${finding.message} (${count} lines)` } : finding,
    );
  }

  items.sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
  );

  const hiddenCount = limit !== undefined && items.length > limit ? items.length - limit : 0;
  const visible = limit !== undefined ? items.slice(0, limit) : items;

  if (visible.length === 0) {
    if (!emptyTitle) return null;
    return <EmptyState compact title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ul className={['finding-list', className].filter(Boolean).join(' ')}>
      {visible.map((finding, index) => (
        <li key={`${finding.code}-${finding.lineId ?? finding.entity ?? index}`} className={`finding finding-${finding.severity}`}>
          <div className="finding-head">
            <Badge tone={finding.severity === 'blocker' ? 'red' : finding.severity === 'warning' ? 'amber' : 'neutral'}>
              {finding.severity === 'blocker' ? 'Blocker' : finding.severity === 'warning' ? 'Warning' : 'Note'}
            </Badge>
            <code className="finding-code">{finding.code}</code>
            {finding.entity && <span className="finding-entity">{finding.entity}</span>}
          </div>
          <p className="finding-message">{finding.message}</p>
          <p className="finding-resolution">
            <span className="finding-resolution-label">To clear this</span>
            {finding.resolution}
          </p>
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className="finding finding-more">
          {hiddenCount} further finding{hiddenCount === 1 ? '' : 's'} not shown.
        </li>
      )}
    </ul>
  );
}

/** Count of findings at each severity. Handy for a heading or a tile. */
export function countFindings(findings: readonly ValidationFinding[]): {
  blocker: number;
  warning: number;
  info: number;
} {
  const out = { blocker: 0, warning: 0, info: 0 };
  for (const f of findings) {
    if (f.severity === 'blocker') out.blocker += 1;
    else if (f.severity === 'warning') out.warning += 1;
    else out.info += 1;
  }
  return out;
}

export default FindingList;
